import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { feedbackService, userService } from '../api';
import { Feedback, User } from '../types';
import Sidebar from '../components/Sidebar';
import './Support.css';

const Support: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<{ [key: number]: User }>({});
  const [resolvingFeedbackId, setResolvingFeedbackId] = useState<number | null>(null);
  const [filter, setFilter] = useState<'ALL' | 'OPEN' | 'RESOLVED'>('ALL');

  useEffect(() => {
    loadFeedbacks();
  }, []);

  const loadFeedbacks = async () => {
    setLoading(true);
    try {
      const feedbackData = await feedbackService.getAllFeedbacks();
      setFeedbacks(feedbackData);

      // Load user details for all unique user IDs
      const userIds = Array.from(new Set(feedbackData.map(f => f.userId)));
      const userMap: { [key: number]: User } = {};

      for (const userId of userIds) {
        try {
          const userData = await userService.getUserById(userId.toString());
          userMap[userId] = userData;
        } catch (error) {
          console.error(`Error loading user ${userId}:`, error);
        }
      }

      setUsers(userMap);
    } catch (error) {
      console.error('Error loading feedbacks:', error);
      setFeedbacks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (feedbackId: number, newStatus: 'OPEN' | 'RESOLVED') => {
    try {
      setResolvingFeedbackId(feedbackId);
      await feedbackService.updateFeedbackStatus(feedbackId, newStatus);

      // Update local state
      setFeedbacks(feedbacks.map(feedback =>
        feedback.id === feedbackId
          ? { ...feedback, status: newStatus }
          : feedback
      ));
    } catch (error) {
      console.error('Error updating feedback status:', error);
      alert('Failed to update feedback status. Please try again.');
    } finally {
      setResolvingFeedbackId(null);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredFeedbacks = feedbacks.filter(feedback => {
    if (filter === 'ALL') return true;
    return feedback.status === filter;
  });

  const openCount = feedbacks.filter(f => f.status === 'OPEN').length;
  const resolvedCount = feedbacks.filter(f => f.status === 'RESOLVED').length;

  return (
    <div className="layout">
      <Sidebar />
      <div className="main-content">
        <div className="top-bar">
          <div className="user-info">
            <span className="user-icon">👤</span>
            <span className="user-email">{currentUser?.email || 'admin@roameez.com'}</span>
          </div>
        </div>

        <div className="page-container">
          <div className="page-header">
            <div>
              <h1 className="page-title">Support & Feedback</h1>
              <div className="breadcrumb">
                <span>Dashboard</span>
                <span className="breadcrumb-separator">›</span>
                <span>Support</span>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="stats-container">
            <div className="stat-card">
              <div className="stat-icon open">📩</div>
              <div className="stat-details">
                <div className="stat-value">{openCount}</div>
                <div className="stat-label">Open Tickets</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon resolved">✅</div>
              <div className="stat-details">
                <div className="stat-value">{resolvedCount}</div>
                <div className="stat-label">Resolved Tickets</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon total">📊</div>
              <div className="stat-details">
                <div className="stat-value">{feedbacks.length}</div>
                <div className="stat-label">Total Feedbacks</div>
              </div>
            </div>
          </div>

          {/* Filter Buttons */}
          <div className="filter-container">
            <button
              className={`filter-btn ${filter === 'ALL' ? 'active' : ''}`}
              onClick={() => setFilter('ALL')}
            >
              All ({feedbacks.length})
            </button>
            <button
              className={`filter-btn ${filter === 'OPEN' ? 'active' : ''}`}
              onClick={() => setFilter('OPEN')}
            >
              Open ({openCount})
            </button>
            <button
              className={`filter-btn ${filter === 'RESOLVED' ? 'active' : ''}`}
              onClick={() => setFilter('RESOLVED')}
            >
              Resolved ({resolvedCount})
            </button>
          </div>

          {loading ? (
            <div className="loading-container">Loading feedbacks...</div>
          ) : filteredFeedbacks.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Ticket ID</th>
                    <th>User</th>
                    <th>Feedback</th>
                    <th>Created On</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFeedbacks.map((feedback) => {
                    const user = users[feedback.userId];
                    return (
                      <tr key={feedback.id}>
                        <td>
                          <span className="ticket-id">#{feedback.id}</span>
                        </td>
                        <td>
                          <div className="user-cell">
                            {user?.profilePic ? (
                              <img
                                src={user.profilePic}
                                alt={user.name}
                                className="table-avatar"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                  if (placeholder) placeholder.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div 
                              className="table-avatar-placeholder"
                              style={{ display: user?.profilePic ? 'none' : 'flex' }}
                            >
                              {user?.name?.charAt(0) || '?'}
                            </div>
                            <div className="user-details">
                              <div className="user-name">{user?.name || 'Unknown User'}</div>
                              <div className="user-id">ID: {feedback.userId}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="feedback-text">{feedback.feedbackText}</div>
                        </td>
                        <td>{formatDateTime(feedback.createdOn)}</td>
                        <td>
                          <span className={`status-badge ${feedback.status.toLowerCase()}`}>
                            {feedback.status}
                          </span>
                        </td>
                        <td>
                          {feedback.status === 'OPEN' ? (
                            <button
                              className="action-btn resolve"
                              onClick={() => handleStatusUpdate(feedback.id, 'RESOLVED')}
                              disabled={resolvingFeedbackId === feedback.id}
                            >
                              {resolvingFeedbackId === feedback.id ? 'Resolving...' : 'Resolve'}
                            </button>
                          ) : (
                            <button
                              className="action-btn reopen"
                              onClick={() => handleStatusUpdate(feedback.id, 'OPEN')}
                              disabled={resolvingFeedbackId === feedback.id}
                            >
                              {resolvingFeedbackId === feedback.id ? 'Reopening...' : 'Reopen'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">
              <p>No {filter.toLowerCase()} feedbacks found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Support;

